import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@client/components/ui";
import { useLang } from "@client/i18n";

// Sources: README "Privacy and retention", db/schema.ts, auth/routes.ts,
// features/teacher/routes.ts and workerApp.ts. Keep claims aligned with those sources.
const content = {
  sv: {
    title: "Integritet och personuppgifter – Modern fysik",
    home: "Modern fysik – startsida",
    contact: "Kontakt och ansvar",
    name: "Sebastian Kilde Westberg",
    sections: [
      {
        title: "Vad som sparas och varför",
        paragraphs: [
          "Modern fysik sparar uppgifter så att du kan öva och följa din utveckling genom att återvända till dina resultat. Uppgifterna gör också att läraren kan följa övningsaktiviteten i kursen.",
          "Vi sparar ditt unika konto-id hos Google (subject-id), din e-postadress och det visningsnamn du väljer. Till kontot hör också dina inställningar, din roll och eventuella bonuspoäng, samt uppgifter om när kontot skapades och senast användes. Svarar du på frågorna i Kom igång sparas svaren också på kontot: var du är i kursen och vilka föreläsningar du missat, vilket betyg du siktar på, hur mycket tid du har per dag och hur säker du känner dig på matematiken. Svaren styr bara hur Dagens pass och övningen ställs in.",
          "När du övar sparas dina försök, svar, poäng och tidpunkter samt uppgifter om hur du använder ledtrådar och lösningar. Även självskattningar, reflektioner, svar och resultat från övningstentor samt begreppskortens repetitionshistorik och repetitionsplan sparas. Detsamma gäller anteckningar och annan fritext som du sparar i övningar och övningstentor. Rapporterar du ett fel i en uppgift sparas rapporten och din kommentar.",
        ],
      },
      {
        title: "Inloggning med Google",
        paragraphs: [
          "Du kan bara logga in med Google och behöver därför ett Google-konto. Ett gratis konto räcker och kan skapas med valfri e-postadress. Vi begär endast behörigheterna openid och email. Googles inloggningstoken, profilbild, Google-profil och Workspace-domän sparas inte. Du har inget lösenord hos oss.",
          "Google är en självständig identitetsleverantör och får veta när du loggar in på Modern fysik.",
        ],
      },
      {
        title: "Vad läraren ser",
        paragraphs: [
          "I lärarvyn visas ditt visningsnamn och din e-postadress samt när kontot skapades och senast användes. Läraren ser också vilka uppgifter du har övat på, dina poäng, statistik över din utveckling och resultat från övningstentor. Den e-postadress som visas hör till ditt Google-konto, även om det är en privat Gmail-adress. Läraren kan välja att exportera övningsstatistik med namn och e-postadress till en CSV-fil. Exporten sker inte automatiskt.",
          "Lärarvyn och CSV-exporten visar inte dina privata anteckningar, fritextsvar, motiveringar eller reflektioner i övningar och övningstentor. Om du rapporterar ett fel i en uppgift ser läraren rapporten och din kommentar tillsammans med ditt visningsnamn. Lärarvyn ger inte tillgång till uppgifter från andra kurser.",
          "Dina svar i Kom igång visas aldrig för läraren per student. Läraren ser bara sammanräknade antal för hela gruppen, till exempel hur många som siktar på VG eller har 10 minuter per dag.",
        ],
      },
      {
        title: "Drift",
        paragraphs: ["Webbplatsen drivs hos Cloudflare. Inga data skickas utanför webbplatsen."],
      },
      {
        title: "Lagringstid och radering",
        paragraphs: [
          "Konton som har varit inaktiva i mer än 18 månader raderas automatiskt vid en daglig körning i Cloudflare Workers. Vid drift med Node görs samma rensning med ett administrativt kommando.",
          "Du kan själv radera ditt konto i Inställningar. Om den aktuella inloggningen är minst tio minuter gammal behöver du först logga in med Google igen. När kontot raderas tas kontots övningsförsök, övningstentor, sparade anteckningar och reflektioner, begreppskortsdata och inloggningssessioner bort. Felrapporter och deras kommentarer finns kvar, men kopplingen till kontot tas bort.",
          "Påbörjade övningssvar och ditt pågående pass i Dagens pass sparas i den här webbläsaren i upp till 7 dagar, så att du kan fortsätta senare. De rensas när du loggar ut.",
        ],
      },
    ],
  },
  en: {
    title: "Privacy and personal data – Modern fysik",
    home: "Modern fysik – home",
    contact: "Contact and responsibility",
    name: "Sebastian Kilde Westberg",
    sections: [
      {
        title: "What is stored and why",
        paragraphs: [
          "Modern fysik stores data so you can practise, return to your results and follow your progress, and so the teacher can follow practice activity in the course.",
          "We store your Google account's unique subject ID, your email address and the display name you choose. Your account also contains settings, your role, any bonus points, and account creation and last activity times. If you answer the Get started questions, the answers are stored on your account too: where you are in the course and which lectures you missed, the grade you aim for, the time you have per day and how confident you feel about the maths. They only set up Today's session and practice.",
          "We store practice attempts, answers, scores and times, use of hints and solutions, self-assessments and reflections, practice exam answers and results, and flashcard review history and schedules. Notes and other free text you save in practice and practice exams are also stored. If you report a problem with a question, the report and your comment are stored.",
        ],
      },
      {
        title: "Google sign-in",
        paragraphs: [
          "Sign-in is only through Google and requires a Google account. A free account is enough and can be created with any email address. We request only the openid and email scopes. We do not store Google's sign-in tokens, profile picture, Google profile or Workspace domain. You have no password on this platform.",
          "Google is an independent identity provider and learns that you sign in to Modern fysik and when.",
        ],
      },
      {
        title: "What the teacher can see",
        paragraphs: [
          "The teacher dashboard shows your display name and email address, account creation and last activity times, which questions you practised, scores, progress statistics and practice exam results. The email shown belongs to your Google account, including a private Gmail address. The teacher can explicitly export practice statistics with names and email addresses to CSV; exports are not automatic.",
          "The teacher dashboard and CSV export do not show your private notes, free-text answers, explanations or reflections in practice and practice exams. Reports and comments you send using the question issue reporting feature are visible to the teacher with your display name. The dashboard does not provide access to data from other courses.",
          "Your Get started answers are never shown to the teacher per student. The teacher sees only totals for the whole group, for example how many aim for VG or have 10 minutes per day.",
        ],
      },
      {
        title: "Hosting",
        paragraphs: ["The website is hosted by Cloudflare. No data is sent outside the website."],
      },
      {
        title: "Retention and deletion",
        paragraphs: [
          "Accounts inactive for more than 18 months are automatically deleted by a daily job in Cloudflare Workers. When running on Node, the equivalent cleanup uses an administrative command.",
          "You can delete your account in Settings (Inställningar). If your current sign-in is at least ten minutes old, you must first sign in with Google again. Deleting the account removes its practice attempts, practice exams, saved notes and reflections, flashcard data and sign-in sessions. Question issue reports and their comments remain, but their link to the account is removed.",
          "Unfinished practice answers and your Dagens pass progress are kept in this browser for up to 7 days so you can continue later. They are cleared when you sign out.",
        ],
      },
    ],
  },
};

export function PrivacyPage() {
  const { lang: appLang } = useLang();
  // This public page needs an English toggle even while the app forces Swedish.
  const [lang, setLang] = useState(appLang);
  const text = content[lang];
  return (
    <main lang={lang} className="mx-auto max-w-3xl px-4 py-12 text-ink-800 dark:text-ink-100">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <Link to="/" className="text-accent-700 underline dark:text-accent-300">
          {text.home}
        </Link>
        <Button variant="ghost" onClick={() => setLang(lang === "sv" ? "en" : "sv")}>
          <span lang={lang === "sv" ? "en" : "sv"}>{lang === "sv" ? "English" : "Svenska"}</span>
        </Button>
      </div>
      <h1 className="font-display text-3xl">{text.title}</h1>
      {text.sections.map((section) => (
        <section key={section.title} className="mt-8">
          <h2 className="font-display text-xl">{section.title}</h2>
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph} className="mt-3 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </section>
      ))}
      <section className="mt-8">
        <h2 className="font-display text-xl">{text.contact}</h2>
        <p className="mt-3">
          {text.name},{" "}
          <a
            className="break-words text-accent-700 underline dark:text-accent-300"
            href="mailto:mindyourphysics.seb@gmail.com"
          >
            mindyourphysics.seb@gmail.com
          </a>
        </p>
      </section>
    </main>
  );
}
